import type { FacetValueFull } from 'Models/index';
import Step, { type StepInput } from 'Models/step';
import { type MainConfig, mainConfig } from 'Models/uiConfig/componentlessWidgetsConfig';
import type { RuntimeConfigMap } from 'Models/uiConfig/requestTypes';
import { DisplayMode } from 'Modules/serverApi/types';
import { isObjectRecord } from 'Utils/guards';

export type FacetSliderMode = '' | 'Range' | 'LowerBound' | 'UpperBound' | 'Lock';

export type FacetSliderConfig = {
  mode?: FacetSliderMode;
};

export interface FacetCaseConfig extends RuntimeConfigMap {
  type: string;
}

export type FacetValueProcessorOptions = RuntimeConfigMap;

export type FacetConfigItem = RuntimeConfigMap & {
  fields?: string[];
  childFields?: string[];
  singleFacet?: boolean;
  templates?: Record<string, FacetConfigItem>;
  cases?: FacetCaseConfig[];
  name?: string;
  type?: string;
  step?: StepInput;
  paletteColors?: Array<[string, string]>;
  slider?: FacetSliderConfig;
  useNativeDropdown?: boolean;
  showHitCount?: boolean;
  customValuesProcessor?: (values: FacetValueFull[], opts: FacetValueProcessorOptions) => FacetValueFull[];
  term?: string;
  initToggled?: boolean;
  isInverted?: boolean;
  getInitCase?: () => number;
};

type FacetConfigValue = FacetConfigItem | FacetConfigItem[];

export type ResolvedFacetConfig = FacetConfigItem & {
  name: string;
  type: string;
};

export type ToggleFacetConfig = ResolvedFacetConfig & {
  type: 'toggleFacet';
  term: string;
};

class FacetsConfig {
  _config: MainConfig;
  _defaultFacets: Record<string, FacetConfigItem> = {};
  _parentFacets: Record<string, { childFields: string[]; singleFacet?: boolean }> = {};
  subFacetFields: string[] = [];

  constructor(config: MainConfig) {
    this._config = config;

    this._setDefaultFacets();
    this._setParentFacets();
    this._setSubFacetFields(this._parentFacets);
  }

  _setDefaultFacets() {
    this._defaultFacets = this._config.Widgets.reduce<Record<string, FacetConfigItem>>((map, widget) => {
      if (hasDefaultFacetConfig(widget)) {
        map[widget.name] = widget.defaultFacetConfig;
      }
      return map;
    }, {});
  }

  _setParentFacets() {
    Object.values(this.configs).forEach((config) => {
      if (isParentFacetConfig(config)) {
        config.fields.forEach((field) => {
          this._parentFacets[field] = { childFields: config.childFields, singleFacet: config.singleFacet };
        });
      }
    });
  }

  _setSubFacetFields(parentFacets: Record<string, { childFields: string[]; singleFacet?: boolean }>) {
    this.subFacetFields = Object.keys(parentFacets).reduce((subFacetFields: string[], key) => {
      const { childFields, singleFacet } = parentFacets[key];
      if (singleFacet) {
        subFacetFields.push(...childFields);
      }
      return subFacetFields;
    }, []);
  }

  get configs(): Record<string, FacetConfigValue> {
    return normalizeFacetConfigs(this._config.facets);
  }

  getNamedConfig(name: string): FacetConfigItem | undefined {
    const config = this.configs[name];
    return Array.isArray(config) ? undefined : config;
  }

  get hideSelectedTreeValues() {
    return this._config.FacetsIsTreeSelectedHide;
  }

  getFacetConfig(
    displayMode: DisplayMode,
    field: string,
    facetPanelName?: string,
    caseNumber: number = 0,
    isInFacetDialog?: boolean,
  ): ResolvedFacetConfig {
    const facetConfig = this._getFacetConfig(field, facetPanelName);

    if (field.toLowerCase() === 'review') {
      return resolveFacetConfig(facetConfig, { name: 'reviewFacet', type: 'simpleFacet' });
    }

    if (field.toLowerCase() === 'price') {
      return resolveFacetConfig(facetConfig, { name: 'priceFacet', type: 'rangedFacet' });
    }

    switch (displayMode) {
      case DisplayMode.Ranges:
        return resolveFacetConfig(facetConfig, { name: 'simpleFacet', type: 'simpleFacet' });
      case DisplayMode.Slider: {
        const facetName = facetConfig.name || 'sliderFacet';
        return resolveFacetConfig(facetConfig, { name: facetName, type: 'rangedFacet' });
      }
      case DisplayMode.AdvancedColor:
        return resolveFacetConfig(facetConfig, {
          name: facetConfig.name || 'advancedColorFacet',
          type: 'advancedColorFacet',
        });
      case DisplayMode.Select:
      case DisplayMode.MultiSelect:
        return isInFacetDialog
          ? resolveFacetConfig(facetConfig, { name: 'simpleFacet', type: 'simpleFacet' })
          : resolveFacetConfig(facetConfig, { name: 'selectFacet', type: 'selectFacet' });
      case DisplayMode.Size:
        return resolveFacetConfig(facetConfig, {
          name: facetConfig.name || 'sizeFacet',
          type: 'sizeFacet',
        });
      case DisplayMode.Toggle:
        return resolveFacetConfig(facetConfig, {
          name: facetConfig.name || 'toggleFacet',
          type: 'toggleFacet',
        });
      case DisplayMode.Switch: {
        const switchCase = facetConfig.cases?.[caseNumber] ?? { type: 'simpleFacet' };
        return resolveFacetConfig(facetConfig, {
          name: switchCase.type || 'simpleFacet',
          ...switchCase,
        });
      }
      case DisplayMode.Default:
      case DisplayMode.Radio:
      case DisplayMode.Checkbox:
      case DisplayMode.Tree:
        return resolveFacetConfig(facetConfig, {
          name: facetConfig.name || 'simpleFacet',
          type: 'simpleFacet',
        });
      default:
        return resolveFacetConfig(facetConfig);
    }
  }

  getSwitcherArgs(field: string, facetPanelName?: string) {
    const { type, cases, getInitCase = () => 0 } = this._getFacetConfig(field, facetPanelName);
    const casesCount = type === 'switchFacet' ? (cases?.length ?? 1) : 1;
    return { casesCount, getInitCase };
  }

  getChildrenForField(field: string) {
    return this._parentFacets[field] || { childFields: [] };
  }

  useImperialForField(field: string) {
    const fieldWithStep = this.subFacetFields.includes(field)
      ? Object.keys(this._parentFacets).find((key) => this._parentFacets[key].childFields.includes(field))
      : field;
    const { step } = this._getFacetConfig(fieldWithStep);
    return step && new Step(step).imperial;
  }

  _getFacetConfig(field: string | undefined, facetPanelName?: string): FacetConfigItem {
    const fieldToMatch = field ?? '';
    const defaultConfig = {
      type: 'simpleFacet',
      ...((facetPanelName && this._defaultFacets[facetPanelName]) || {}),
    };

    for (const [type, facetConfig] of Object.entries(this.configs)) {
      const foundConfig = Array.isArray(facetConfig)
        ? facetConfig.find((c) => hasFacetField(c, fieldToMatch))
        : hasFacetField(facetConfig, fieldToMatch) && facetConfig;

      if (foundConfig) {
        return { ...defaultConfig, ...foundConfig, type };
      }
    }

    return defaultConfig;
  }
}

function resolveFacetConfig(
  facetConfig: FacetConfigItem,
  overrides: Partial<ResolvedFacetConfig> = {},
): ResolvedFacetConfig {
  const type = overrides.type || facetConfig.type || 'simpleFacet';

  return {
    ...facetConfig,
    ...overrides,
    name: overrides.name || facetConfig.name || type,
    type,
  };
}

function normalizeFacetConfigs(facets: unknown): Record<string, FacetConfigValue> {
  const configs: Record<string, FacetConfigValue> = {};

  if (!isObjectRecord(facets)) {
    return configs;
  }

  Object.entries(facets).forEach(([key, value]) => {
    if (Array.isArray(value) && value.every(isFacetConfigItem)) {
      configs[key] = value;
    } else if (isFacetConfigItem(value)) {
      configs[key] = value;
    }
  });

  return configs;
}

function hasDefaultFacetConfig(
  widget: MainConfig['Widgets'][number],
): widget is MainConfig['Widgets'][number] & { defaultFacetConfig: FacetConfigItem } {
  return 'defaultFacetConfig' in widget && isFacetConfigItem(widget.defaultFacetConfig);
}

function isParentFacetConfig(
  config: FacetConfigValue,
): config is FacetConfigItem & { childFields: string[]; fields: string[] } {
  return (
    !Array.isArray(config) &&
    isStringArray(config.childFields) &&
    isStringArray(config.fields) &&
    (config.singleFacet === undefined || typeof config.singleFacet === 'boolean')
  );
}

function hasFacetField(config: FacetConfigItem, field: string): boolean {
  return isStringArray(config.fields) && config.fields.includes(field);
}

function isFacetConfigItem(value: unknown): value is FacetConfigItem {
  return isObjectRecord(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

const facetsConfig = new FacetsConfig(mainConfig);
window.Convermax.templates!.config.facetsConfig = facetsConfig;

export default facetsConfig;
